<template>
  <div
    class="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-3"
  >
    <button
      type="button"
      class="rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
      :disabled="saving"
      data-cy="modal-cancel-btn"
      @click="$emit('cancel')"
    >
      Cancel
    </button>
    <button
      type="button"
      :class="primaryClasses"
      :disabled="disabled || saving"
      data-cy="modal-submit-btn"
      @click="$emit('submit')"
    >
      <span v-if="saving" class="inline-flex items-center gap-2">
        <span
          class="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white"
        />
        {{ savingText }}
      </span>
      <span v-else class="inline-flex items-baseline gap-1">
        <span>{{ verb }}</span>
        <span v-if="!hideAmount" class="font-mono font-semibold tabular-nums">{{
          formattedAmount
        }}</span>
        <span v-if="destination"
          >{{ destinationPreposition }} {{ destination }}</span
        >
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { dollars } from "@/mixins";

const props = withDefaults(
  defineProps<{
    tone?: "pay" | "refund";
    amountCents?: number;
    destination?: string;
    saving?: boolean;
    disabled?: boolean;
    savingText?: string;
    verbOverride?: string;
    hideAmount?: boolean;
  }>(),
  {
    tone: "pay",
    amountCents: 0,
    destination: "",
    saving: false,
    disabled: false,
    savingText: "Processing…",
    verbOverride: "",
    hideAmount: false
  }
);

defineEmits<{
  (e: "submit"): void;
  (e: "cancel"): void;
}>();

const verb = computed(() => {
  if (props.verbOverride) return props.verbOverride;
  return props.tone === "refund" ? "Refund" : "Pay";
});

const destinationPreposition = computed(() =>
  props.tone === "refund" ? "to" : "via"
);

const formattedAmount = computed(() => dollars(props.amountCents || 0));

const primaryClasses = computed(() => {
  const base =
    "inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed";
  return props.tone === "refund"
    ? `${base} bg-red-600 hover:bg-red-700 focus-visible:ring-red-300`
    : `${base} bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-300`;
});
</script>
